/**
 * Helper components and building blocks for building sections which support
 * viewing and editing.
 */

import { AutosizeText, AutosizeProps } from '@components/autosize-text';
import { Button } from '@components/buttons';
import { IcoImage, IcoRefresh } from '@components/icons';
import { useImageUrl } from 'client/utils/cdn';
import { useMemo } from 'preact/hooks';
import { RichTextEditor, RichTextViewer, ToolbarPosition } from './rich-text';
import { EditorProps } from './types';
import { PhotoPicker } from './photo-picker';
import { showGlobal } from '@components/dialog/global-render';

export type FlexProps = {
  id?: string;
  path: string | Array<string | number>;
  class?: string;
  isSelected?: boolean;
  placeholder?: string;
  version?: number;
  state: any;
  setState?: EditorProps<any>['setState'];
  selection?: EditorProps<any>['selection'];
  setSelection?: EditorProps<any>['setSelection'];
};

export function getProp(props: FlexProps) {
  if (typeof props.path === 'string') {
    return props.path ? props.state[props.path] : props.state;
  }
  return props.path.reduce((acc, k) => {
    return acc[k];
  }, props.state);
}

export function setProp(props: FlexProps, val: any) {
  if (!props.setState) {
    return;
  }
  const applyStateProp = (state: any) => (typeof val === 'function' ? val(state) : val);
  if (!props.path) {
    props.setState((s: any) => ({ ...s, ...applyStateProp(val) }));
    return;
  }
  const path = typeof props.path === 'string' ? [props.path] : props.path;
  const assoc = (obj: any, [k, ...path]: any[]) => {
    let next = obj[k];
    if (Array.isArray(next)) {
      next = [...next];
    } else if (next && typeof next === 'object') {
      next = { ...next };
    } else {
      next = val;
    }
    if (path.length) {
      obj[k] = next;
      assoc(next, path);
    } else {
      obj[k] = applyStateProp(next);
    }
    return obj;
  };

  props.setState((s: any) => assoc({ ...s }, path));
}

export function RichProp(props: FlexProps & { toolbarPosition?: ToolbarPosition }) {
  const readonly = !props.setState;
  const doc = getProp(props);

  if (readonly) {
    return <RichTextViewer doc={doc} class={props.class} />;
  }

  const { id, setSelection } = props;
  return (
    <RichTextEditor
      doc={doc}
      version={props.version}
      toolbarPosition={props.toolbarPosition}
      class={props.class}
      onChange={(val, selection) => {
        setProp(props, val);
        if (id && setSelection) {
          setSelection({ id, value: selection });
        }
      }}
      isSelected={props.isSelected}
      selection={props.id === props.selection?.id ? props.selection?.value : undefined}
    />
  );
}

function setQueryParam(url: undefined | string, param: string, val: string | number) {
  if (!url) {
    return;
  }
  const u = new URL(url, window.location.origin);
  u.searchParams.set(param, val.toString());
  return u.toString();
}

function NoPic(props: { class?: string }) {
  return (
    <span class={`${props.class} overflow-hidden`}>
      <IcoImage class="w-1/2 h-1/2 max-w-24 mx-auto" />
    </span>
  );
}

export function ImgProp(
  props: FlexProps & {
    wrapperClass?: string;
    width?: string;
    placeholder?: string;
    overlayClass?: string;
  },
) {
  const readonly = !props.setState;
  const rawVal = useImageUrl(getProp(props));
  const val = useMemo(() => setQueryParam(rawVal, 'width', props.width || '600'), [rawVal]);

  if (readonly) {
    return (
      <span class={props.wrapperClass}>
        {val ? (
          <img class={props.class} src={val} onError={() => setProp(props, props.placeholder)} />
        ) : (
          <NoPic class={props.class} />
        )}
      </span>
    );
  }

  async function handlePhotoClick() {
    const result = await showGlobal<string>(({ resolve }) => (
      <PhotoPicker
        isOpen
        onPick={(photoUrl) => {
          const width = parseInt(props.width || '600') * 2;
          const url = setQueryParam(photoUrl, 'w', width);
          resolve(url);
        }}
        onClose={() => resolve(undefined)}
      />
    ));

    if (result) {
      setProp(props, result);
    }
  }

  return (
    <Button
      class={`relative cursor-pointer overflow-hidden ${props.wrapperClass || ''} ${val ? '' : 'bg-gray-100'}`}
      onClick={handlePhotoClick}
    >
      {val && (
        <img class={props.class} src={val} onError={() => setProp(props, props.placeholder)} />
      )}
      {!val && <NoPic class={props.class} />}
      <span
        class={`absolute top-0 left-0 h-full w-full opacity-0 hover:opacity-90 bg-gray-300 text-black flex items-center justify-center transition-all text-xs ${props.overlayClass || 'rounded-full'}`}
      >
        <IcoRefresh class="size-6" />
      </span>
    </Button>
  );
}

export function MultilineProp(props: FlexProps & { inline?: boolean } & AutosizeProps) {
  const readonly = !props.setState;
  const val = getProp(props);
  if (readonly) {
    return <>{val}</>;
  }
  return (
    <AutosizeText
      {...props}
      placeholder={props.placeholder || 'Enter text'}
      containerClass={`text-inherit inline-block ${props.inline ? 'min-w-1' : 'w-full'}`}
      class={`border-transparent placeholder-inherit rounded border p-0 text-inherit break-normal inline-block ${
        props.class || ''
      }`}
      onInput={(e: any) => setProp(props, e.target.value)}
      value={val}
    />
  );
}
